import { useEffect, useRef, useState } from 'react';

interface UseVideoTrackPreviewProps {
  videoUrl: string;
  isPlaying: boolean;
  onPlayingChange: (playing: boolean) => void;
}

interface UseVideoTrackPreviewReturn {
  videoContainerRef: React.RefObject<HTMLDivElement>;
  progress: number;
  videoDuration: number;
  hasAudio: boolean;
  isLoaded: boolean;
  isBuffering: boolean;
}

/**
 * Hook for managing video track preview playback with audio detection
 *
 * Features:
 * - Creates and manages video element
 * - Handles play/pause with audio support (falls back to muted if autoplay blocked)
 * - Detects audio using Web Audio API (captureStream method)
 * - Tracks playback progress and duration
 * - Cleans up resources properly
 */
export const useVideoTrackPreview = ({
  videoUrl,
  isPlaying,
  onPlayingChange
}: UseVideoTrackPreviewProps): UseVideoTrackPreviewReturn => {
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [hasAudio, setHasAudio] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  // Audio detection refs
  const hasAudioRef = useRef(false);
  const audioCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioAnalysisCleanupRef = useRef<(() => void) | null>(null);

  // Create and manage video element
  useEffect(() => {
    const container = videoContainerRef.current;
    if (!container) {
      return;
    }

    // Create video element if it doesn't exist
    if (!videoRef.current) {
      const video = document.createElement('video');
      video.setAttribute('data-testid', 'video-track-card-preview');
      video.preload = 'metadata';
      video.playsInline = true;
      video.loop = true;
      video.crossOrigin = 'anonymous';
      video.className = 'absolute inset-0 size-full object-cover';
      video.style.zIndex = '1';
      video.src = videoUrl;
      video.currentTime = 0;

      const errorHandler = () => {
        console.error('Failed to load video:', videoUrl);
        onPlayingChange(false);
      };

      const endedHandler = () => {
        video.pause();
        onPlayingChange(false);
        setProgress(0);
      };

      // Audio analysis state (scoped to this video element)
      let audioContext: AudioContext | null = null;
      let analyser: AnalyserNode | null = null;
      let sourceNode: MediaStreamAudioSourceNode | null = null;
      let mediaStream: MediaStream | null = null;

      /**
       * Analyze actual audio signal levels using captureStream()
       * This detects if there's audible audio content, not just an audio track
       */
      const analyzeAudioSignal = (): boolean => {
        if (!analyser) {
          return false;
        }

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        // Calculate average frequency amplitude
        const sum = dataArray.reduce((acc, val) => acc + val, 0);
        const average = sum / bufferLength;

        // Threshold of 5 filters out silent tracks while detecting actual audio
        // Silent audio tracks typically have values near 0
        return average > 5;
      };

      /**
       * Set up audio analysis using captureStream()
       * This doesn't affect the video's audio output - it just captures for analysis
       */
      const setupAudioAnalysis = () => {
        try {
          // Check if captureStream is supported
          const videoWithCapture = video as HTMLVideoElement & {
            captureStream?: () => MediaStream;
            mozCaptureStream?: () => MediaStream;
          };

          const captureMethod = videoWithCapture.captureStream || videoWithCapture.mozCaptureStream;
          if (!captureMethod) {
            return false;
          }

          // Capture the video's media stream
          mediaStream = captureMethod.call(video);
          const audioTracks = mediaStream.getAudioTracks();

          // No audio tracks in the stream
          if (audioTracks.length === 0) {
            return false;
          }

          // Create audio context and analyser
          const AudioContextClass =
            window.AudioContext ||
            (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
          if (!AudioContextClass) {
            return false;
          }

          audioContext = new AudioContextClass();
          analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.8;

          // Connect the stream to the analyser (doesn't affect video audio output)
          sourceNode = audioContext.createMediaStreamSource(mediaStream);
          sourceNode.connect(analyser);
          // Note: We don't connect to destination, so this is silent analysis

          return true;
        } catch {
          // Expected to fail for cross-origin videos without CORS headers
          // In this case, we simply don't show the audio indicator (no false positives)
          return false;
        }
      };

      /**
       * Clean up audio analysis resources
       */
      const cleanupAudioAnalysis = () => {
        if (sourceNode) {
          sourceNode.disconnect();
          sourceNode = null;
        }
        if (analyser) {
          analyser.disconnect();
          analyser = null;
        }
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close().catch(() => {
            // Cleanup — close failure is harmless; the context is being discarded.
          });
          audioContext = null;
        }
        if (mediaStream) {
          mediaStream.getTracks().forEach(track => track.stop());
          mediaStream = null;
        }
      };

      // Store cleanup function in ref for access during effect cleanup
      audioAnalysisCleanupRef.current = cleanupAudioAnalysis;

      const loadedMetadataHandler = () => {
        if (video.duration) {
          setVideoDuration(video.duration);
        }
      };

      // Detect actual audible audio during playback
      const playingHandler = () => {
        // If already detected audio, no need to check again
        if (hasAudioRef.current) {
          return;
        }

        // Clear any existing interval
        if (audioCheckIntervalRef.current) {
          clearInterval(audioCheckIntervalRef.current);
          audioCheckIntervalRef.current = null;
        }

        // Set up audio analysis if not already done
        if (!analyser) {
          const setupSuccessful = setupAudioAnalysis();
          if (!setupSuccessful) {
            return;
          }
        }

        // Resume audio context if suspended
        if (audioContext && audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {
            // Best-effort resume — audio analysis just won't run if this fails.
          });
        }

        // Poll for actual audio signal a few times
        let checkCount = 0;
        const maxChecks = 10; // Check for 1 second total
        audioCheckIntervalRef.current = setInterval(() => {
          checkCount++;

          if (analyzeAudioSignal()) {
            hasAudioRef.current = true;
            setHasAudio(true);
            if (audioCheckIntervalRef.current) {
              clearInterval(audioCheckIntervalRef.current);
              audioCheckIntervalRef.current = null;
            }
          } else if (checkCount >= maxChecks) {
            // No audible audio detected after multiple checks
            if (audioCheckIntervalRef.current) {
              clearInterval(audioCheckIntervalRef.current);
              audioCheckIntervalRef.current = null;
            }
          }
        }, 100);
      };

      const loadedDataHandler = () => {
        if (video.currentTime !== 0) {
          video.currentTime = 0;
        }
      };

      const canPlayThroughHandler = () => {
        setIsLoaded(true);
        setIsBuffering(false);
      };

      const waitingHandler = () => {
        setIsBuffering(true);
      };

      const canPlayHandler = () => {
        setIsBuffering(false);
      };

      video.addEventListener('error', errorHandler);
      video.addEventListener('canplaythrough', canPlayThroughHandler);
      video.addEventListener('waiting', waitingHandler);
      video.addEventListener('canplay', canPlayHandler);
      video.addEventListener('ended', endedHandler);
      video.addEventListener('loadedmetadata', loadedMetadataHandler);
      video.addEventListener('loadeddata', loadedDataHandler);
      video.addEventListener('playing', playingHandler);

      container.appendChild(video);
      videoRef.current = video;
    }

    // Cleanup on unmount
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      // Cleanup audio check interval
      if (audioCheckIntervalRef.current) {
        clearInterval(audioCheckIntervalRef.current);
        audioCheckIntervalRef.current = null;
      }
      // Cleanup audio analysis resources
      if (audioAnalysisCleanupRef.current) {
        audioAnalysisCleanupRef.current();
        audioAnalysisCleanupRef.current = null;
      }
      if (videoRef.current) {
        const video = videoRef.current;
        video.pause();
        video.remove();
        videoRef.current = null;
      }
    };
  }, [videoUrl, onPlayingChange]);

  // Smooth progress updates using requestAnimationFrame
  useEffect(() => {
    if (!isPlaying || !videoRef.current || !videoDuration) {
      // Cancel any running animation frame
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const updateProgress = () => {
      const video = videoRef.current;
      if (video && videoDuration && !video.paused) {
        const currentProgress = (video.currentTime / videoDuration) * 100;
        setProgress(currentProgress);
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      } else {
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isPlaying, videoDuration]);

  // Handle play/pause based on isPlaying state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (isPlaying) {
      // Try to play with audio first (user interaction allows this)
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Successfully started playing - audio will play if available
          })
          .catch(() => {
            // Autoplay policy blocked - try muted playback as fallback
            video.muted = true;
            video
              .play()
              .then(() => {
                // Playing muted as fallback
              })
              .catch(mutedError => {
                console.error('Failed to play video (even muted):', mutedError);
                onPlayingChange(false);
              });
          });
      }
    } else {
      video.pause();
      // Reset muted state when pausing so next play attempt can try with audio
      video.muted = false;
    }
  }, [isPlaying, onPlayingChange]);

  return {
    videoContainerRef: videoContainerRef as React.RefObject<HTMLDivElement>,
    progress,
    videoDuration,
    hasAudio,
    isLoaded,
    isBuffering
  };
};
